/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { isEmpty } from 'lodash';
import { Theme as MaterialUITheme } from '@rjsf/material-ui';
import { useDispatch } from 'react-redux';
import { People, Report } from '@material-ui/icons';
import useScrollToTopIfHashNotSet from 'shared/utils/hooks/useScrollToTopIfHashNotSet';
import DraftFooter from '../../components/submission/DraftFooter';
import { useSigningForm } from './useSigningForm';
import { fields, templates, widgets } from '../../components/submission/templates';
import LoadingPreview from '../../components/submission/LoadingPreview';
import withTheme from '../../components/submission/rjsf/withTheme';
import { ProcedureCategory } from '../../procedure/SubmissionProcedure';
import DraftDrawer from '../../components/submission/DraftDrawer';
import { getSubmissionTitle } from '../../shared/submission/submissionPropertyAccessor';
import StatusChip from '../../components/common/StatusChip';
import Breadcrumb from '../../components/common/Breadcrumb';
import { SubmissionContextProvider } from '../../components/submission/context/SubmissionContextProvider';
import Button from '../../components/common/Button';
import { getResendSigningEmailsThunk } from '../../redux/features/submission/submission.thunk.signing';
import { getSubmissionStatusId } from '../../models/SubmissionStatus';

// import { callApi } from '../../api/helpers/api.helper';
// import draftApi from '../../api/draft.api';

const MuiForm = withTheme(MaterialUITheme);

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  heading: {
    marginBottom: '1rem',
    position: 'relative',
  },
  buttonBackToDraft: {
    marginRight: '1rem',
  },
  buttonWrapper: {
    position: 'absolute',
    right: 0,
    top: '10rem',
  },
  paper: {
    margin: '0',
    padding: '0',
    backgroundColor: theme.palette.elementBackground,
    boxShadow: 'none',
    borderRadius: '1rem',
  },
  whitePaper: {
    marginBottom: '4rem',
    padding: '1rem 1rem',
    backgroundColor: theme.palette.white,
    boxShadow: '0rem 0.0625rem 0.1875rem rgba(63, 63, 68, 0.15)',
    borderRadius: '0.25rem',
    [theme.breakpoints.up('md')]: {
      padding: '2rem 3rem',
    },
    [theme.breakpoints.up('xl2')]: {},
  },
}));

// delete this when the right hand menu will be fully implemented
const useInformationStyles = makeStyles(theme => ({
  containerError: {},
  sectionError: {
    display: 'flex',
    marginTop: '2rem',
    fontSize: '2em',
    color: theme?.palette?.error?.main,
    backgroundColor: theme?.palette?.error?.background,
    borderRadius: '0.2rem',
    padding: '1rem 0.2rem',
    border: `1px solid ${theme?.palette?.error?.main}`,
  },
  sectionErrorDisplay: {
    display: 'flex',
  },
  errorIcon: {
    display: 'flex',
    color: theme?.palette?.error?.main,
    marginRight: '0.5rem',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    fontSize: '1.25rem',
    fontWeight: 1000,
    marginBottom: '1rem',
    color: theme?.palette?.mainText,
  },
}));

/**
 * Payment screen that presents the submission within a sliced payment only RJS form.
 * @returns {JSX.Element}
 * @constructor
 */
export const SigningForm = () => {
  document.title = 'Draft Submission - Patent E-filing';
  const classes = useStyles();
  const informationClasses = useInformationStyles();

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    procedure,
    submissionId,
    submission,
    validationSchema,
    uiSchema,
    workflowValidations,
    errors,
    onSave,
    onSaveAndClose,
    onClose,
    onSendToPackage,
    onValidate,
    onSectionChange,
    isRightMenuOpen,
    toggleRightMenu,
    isSigningPage,
    user,
    currentOrganization,
  } = useSigningForm();

  useScrollToTopIfHashNotSet();

  const idPrefix = 'signature';

  const emailsNotSent = submission?.signatures?.emailsNotSent;
  return (
    <>
      <div className="submissionContainer">
        <Breadcrumb className="breadcrumbs" currentBreadcrumb={t('submission.sign')} />

        <div className={classes.heading} id="sign-submission-header">
          <div id="sign-submission-header">
            <Typography variant="h1">{t('submission.sign')}</Typography>

            <Grid container>
              <Grid
                container item justify="flex-start" alignItems="center" spacing={2}
                xs={6}
              >
                <Grid item>
                  <Typography variant="h3" className={classes.customH3}>
                    {getSubmissionTitle(submission, uiSchema)}
                  </Typography>
                </Grid>

                {submission?.filter?.statusOrder && (
                  <Grid item>
                    <StatusChip status={getSubmissionStatusId(submission.filter.statusOrder)} addHiddenInfo={true} />
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid container className={classes.headerContent}>
              <Grid item md={8}>
                <Typography>
                  {procedure.category === ProcedureCategory.SUBSEQUENT_ACTIONS
                    ? t('signature.title.no.parties')
                    : t('signature.title.parties')}
                </Typography>
              </Grid>
            </Grid>

            {/* delete this when the right hand menu will be fully implemented */}
            {emailsNotSent ? (
              <Grid container className={classes.headerContent}>
                <Grid item md={4}>
                  <Grid item className={informationClasses.sectionError}>
                    <Grid item md={1} className={informationClasses.errorIcon}>
                      <Report />
                    </Grid>
                    <Grid item md={11} direction="row" alignItems="center">
                      <div className={informationClasses.sectionErrorDisplay}>
                        <Typography className={informationClasses.errorText} variant="body1" data-testid="info-text">
                          <Trans i18nKey="error.signature.failed.sending.emails" />
                        </Typography>
                      </div>
                      <div className={informationClasses.sectionErrorDisplay}>
                        <Button
                          startIcon={<People />}
                          onClick={() => {
                            dispatch(getResendSigningEmailsThunk({ submissionId }));
                          }}
                        >
                          {t('submission.sign.resend.emails')}
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ) : null}
          </div>
        </div>

        {!isEmpty(validationSchema) && !isEmpty(uiSchema) && !isEmpty(submission) && !isEmpty(user) ? (
          <SubmissionContextProvider>
            <MuiForm
              translation={t}
              className={classes.form}
              idPrefix={idPrefix}
              id={idPrefix}
              schema={validationSchema}
              uiSchema={uiSchema}
              fields={fields}
              widgets={widgets}
              templates={templates}
              formData={submission}
              formContext={{
                classes,
                submissionId,
                schema: validationSchema,
                attachments: submission?.attachments,
                submission,
              }}
              onChange={onValidate}
              onBlur={onValidate}
              onSectionChange={onSectionChange}
              showErrorList={false}
            >
              <></>
            </MuiForm>
          </SubmissionContextProvider>
        ) : (
          <LoadingPreview />
        )}
      </div>

      <DraftFooter
        readOnly={false}
        submission={submission}
        submissionId={submission?.submissionId}
        errors={errors}
        isValid={workflowValidations?.isValid}
        uiSchema={uiSchema}
        onSave={onSave}
        onSaveAndClose={onSaveAndClose}
        isSigningPage={isSigningPage}
        isPaymentPage={() => false}
        onSendToPackage={onSendToPackage}
        onClose={onClose}
        isDrawerOpen={isRightMenuOpen}
        toggleDrawer={toggleRightMenu}
        closeDialog={() => {}}
        isDialogOpen={false}
      />

      <DraftDrawer
        user={user}
        currentOrganization={currentOrganization}
        submission={submission}
        readOnly={false}
        uiSchema={uiSchema}
        sectionValidations={workflowValidations?.sectionValidations}
        isDrawerOpen={isRightMenuOpen}
        toggleDrawer={toggleRightMenu}
      />
    </>
  );
};
